import { Column, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { DashedSeparator } from '~/commonComponents';
import { IReferrer, ReferralStatus } from '~/domain/Referral';
import { IReferral, ReferralListStatuses, UserTypeEnum } from '~/models';
import { EmptyReferral } from './EmptyReferral';

import BriefcaseIcon from '~/images/briefcase.svg';
import CalendarCheckIcon from '~/images/calendar-check-icon.svg';
import DollarIcon from '~/images/dollar-icon.svg';
import HourglassIcon from '~/images/hourglass.svg';
import HouseIcon from '~/images/house-icon.svg';
import MemoCircleCheckIcon from '~/images/memo-circle-check.svg';
import UserIcon from '~/images/user-icon.svg';

interface IReferralListProps {
  data: IReferral[];
  style?: React.CSSProperties;
  referrer: IReferrer | null;
}

export const ReferralList = ({ data = [], referrer, style }: IReferralListProps) => {
  const showReferredMeSection = !!referrer;

  const referralStatusIconMap = {
    [ReferralListStatuses.AccountCreated]: MemoCircleCheckIcon,
    [ReferralListStatuses.FirstShiftScheduled]: CalendarCheckIcon,
    [ReferralListStatuses.FirstShiftCompleted]: BriefcaseIcon,
    [ReferralListStatuses.PaymentSent]: DollarIcon,
  };

  const referralStatusOrder = [
    ReferralListStatuses.AccountCreated,
    ReferralListStatuses.FirstShiftScheduled,
    ReferralListStatuses.FirstShiftCompleted,
    ReferralListStatuses.PaymentSent,
  ];

  const renderStatusIcon = (status: ReferralListStatuses) => {
    const Icon = referralStatusIconMap[status];

    return Icon ? (
      <img src={Icon} alt="icon" style={{ width: 19, height: 19, marginLeft: 8 }} />
    ) : null;
  };

  const getReferralStatusText = (status: ReferralListStatuses) => {
    return status.replace('First', '1st');
  };

  const orderedData = data.sort((a, b) => {
    return (
      referralStatusOrder.indexOf(a.referralStatus) - referralStatusOrder.indexOf(b.referralStatus)
    );
  });

  return (
    <Column style={{ width: 549, ...style }}>
      {(showReferredMeSection || data?.length > 0) && (
        <Typography
          italic
          color={colors.neutral[500]}
          style={{ fontSize: 16, textAlign: 'center', marginTop: 24, marginBottom: 8 }}
        >
          Track their progress step by step here: Account Created, Shift Scheduled,
          <br />
          Shift Completed, Payment Sent.
        </Typography>
      )}

      {showReferredMeSection && (
        <Column style={{ marginTop: 16 }}>
          <Row align="center" gap={3} pb={5}>
            <DashedSeparator width={180} />
            <Typography as="span" bold size="body_large" color={colors.primary[500]}>
              Who Referred Me
            </Typography>
            <DashedSeparator width={180} />
          </Row>

          <Row align="center" justify="between">
            <Row align="center">
              <img src={UserIcon} alt="icon" style={{ width: 24, height: 24, marginRight: 5 }} />
              <Typography as="span" bold numberOfLines={1} style={{ maxWidth: 320 }}>
                {referrer.name}
              </Typography>
            </Row>

            <Row align="center">
              <Typography as="span" size="body_large" color={colors.neutral[500]}>
                {referrer.status === ReferralStatus.Paid ? 'Payment Sent' : 'In Progress'}
              </Typography>
              <img
                src={referrer.status === ReferralStatus.Paid ? DollarIcon : HourglassIcon}
                alt="icon"
                style={{ width: 19, height: 19, marginLeft: 5 }}
              />
            </Row>
          </Row>
        </Column>
      )}

      {showReferredMeSection && (
        <Column
          align="center"
          justify="center"
          style={{ alignSelf: 'center', width: '100%', marginTop: 32 }}
        >
          <Row align="center" gap={3}>
            <DashedSeparator width={205} />
            <Typography as="span" bold size="body_large" color={colors.primary[500]}>
              My Referrals
            </Typography>
            <DashedSeparator width={205} />
          </Row>
        </Column>
      )}

      {data?.length === 0 && <EmptyReferral />}

      {data?.length > 0 && (
        <>
          {orderedData.map((item, idx) => (
            <Row
              key={item.id}
              pt={6}
              pb={5}
              align="center"
              justify="between"
              style={{
                borderBottom: idx === data.length - 1 ? 'none' : '1px solid #EDEDED',
              }}
            >
              <Row align="center">
                <img
                  src={item.referredUserType === UserTypeEnum.DNT ? HouseIcon : UserIcon}
                  alt={item.referredUserType === UserTypeEnum.DNT ? 'house-icon' : 'user-icon'}
                  style={{ width: 24, height: 24, marginRight: 5 }}
                />
                <Typography as="span" bold>{item.referredName}</Typography>
              </Row>

              <Row align="center">
                <Typography as="span" color={colors.neutral[500]}>{getReferralStatusText(item.referralStatus)}</Typography>
                {renderStatusIcon(item.referralStatus)}
              </Row>
            </Row>
          ))}
        </>
      )}
    </Column>
  );
};
